import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import { Input } from "@common/components/ui/input"
import { cn } from "@common/lib/utils"
import type { ComponentPropsWithoutRef } from "react"
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"
import { FieldRow } from "./row"

type BareProps<T extends FieldValues> = Omit<
  ComponentPropsWithoutRef<typeof Input>,
  "name" | "type" | "value" | "defaultValue" | "onChange" | "onBlur"
> & {
  name: Path<T>
  optional?: boolean
}

export function BareNumberField<T extends FieldValues>({
  name,
  optional,
  className,
  ...rest
}: BareProps<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) =>
        renderNumberInput<T>({
          field,
          invalid: !!fieldState.error,
          optional,
          className,
          ...rest,
        })
      }
    />
  )
}

function renderNumberInput<T extends FieldValues>({
  field,
  invalid,
  optional,
  className,
  ...rest
}: Omit<BareProps<T>, "name"> & {
  field: ControllerRenderProps<T, Path<T>>
  invalid: boolean
}) {
  return (
    <Input
      type="number"
      className={cn(
        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        className,
      )}
      aria-required={!optional}
      aria-invalid={invalid ? true : undefined}
      {...rest}
      name={field.name}
      ref={field.ref}
      onBlur={field.onBlur}
      value={field.value ?? ""}
      onChange={(e) => {
        const raw = e.target.value
        field.onChange(raw === "" ? null : e.target.valueAsNumber)
      }}
    />
  )
}

type Props<T extends FieldValues> = BareProps<T> & { label: string }

export function NumberField<T extends FieldValues>({
  name,
  label,
  className,
  optional,
  ...rest
}: Props<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FieldLabel label={label} optional={optional} />
          <FormControl>
            {renderNumberInput<T>({
              field,
              invalid: !!fieldState.error,
              optional,
              ...rest,
            })}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function RowNumberField<T extends FieldValues>({
  name,
  label,
  className,
  optional,
  ...rest
}: Props<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn("space-y-0", className)}>
          <FieldRow
            label={
              <FieldLabel
                label={label}
                optional={optional}
                className="text-body-medium"
              />
            }
          >
            <FormControl>
              {renderNumberInput<T>({
                field,
                invalid: !!fieldState.error,
                optional,
                ...rest,
              })}
            </FormControl>
            <FormMessage />
          </FieldRow>
        </FormItem>
      )}
    />
  )
}
